/**
 * Loads the Google Maps JavaScript API and reports how that went.
 *
 * The application used to call `bootstrapApplication` from this script's `onload` and nowhere
 * else, so a slow, blocked, rate-limited or simply unreachable `maps.googleapis.com` meant Angular
 * never booted at all — a blank page on *every* route, including sign-in, tickets, drivers,
 * settings and reports, none of which draw a map (#632). Only one screen needs Maps; the rest of
 * the product should not be hostage to a third party it does not control.
 *
 * So this never rejects. It settles exactly once, with the reason, and the caller boots either
 * way. `'error'` and `'timeout'` mean the `google.maps` global is (probably) absent, which the
 * dispatch map handles by showing a fallback instead of instantiating `<google-map>` — the
 * `GoogleMap` component throws "Namespace google not found" if constructed without it.
 *
 * A `'timeout'` is not an abort: the browser keeps fetching, and the script may still install the
 * global afterwards. It only bounds how long first paint waits on the network.
 */
export type GoogleMapsLoadResult = 'loaded' | 'error' | 'timeout';

/**
 * How long to wait for Maps before booting without it.
 *
 * Long enough that a merely slow connection still gets a working map on the first try, short
 * enough that a black-holed request (no RST, no DNS failure — `onerror` never fires) does not hold
 * the entire product on a white screen. A TCP connect to a dropped packet can hang far longer.
 */
export const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 8000;

export function loadGoogleMaps(
  src: string,
  timeoutMs: number = GOOGLE_MAPS_LOAD_TIMEOUT_MS,
): Promise<GoogleMapsLoadResult> {
  return new Promise<GoogleMapsLoadResult>((resolve) => {
    let settled = false;

    const settle = (result: GoogleMapsLoadResult): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      resolve(result);
    };

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => settle('loaded');
    script.onerror = () => settle('error');

    // Declared after `settle` so both can close over each other; the timer cannot fire before the
    // synchronous remainder of this executor has run.
    const timeoutId = setTimeout(() => settle('timeout'), timeoutMs);

    document.head.appendChild(script);
  });
}
